import { getFont } from '../state';
import { isNumeric } from '../utils/isNumeric';
import { scaleFont } from '../utils/scaleFont';

import { buildDefaultMulti } from './buildDefaultMulti';
import { Direction } from './direction';
import type { Line } from './Line';
import { MathObject } from './MathObject';
import type { Multi, MultiDependencies } from './Multi';
import { Text } from './Text';

const POWER_SCALE = 0.7;
const OFFSET_RATIO = 0.75;
const MAX_CHARS_IN_EXP_TEXT = 8;

export class Power extends MathObject {
  base: Multi;
  exp: Multi;

  constructor(line: Line, base: Multi, exp: Multi) {
    super();

    this.t = 'power';
    this.gmmName = 'power';

    this.base = base;
    this.base.gmmName = 'base multi';
    this.exp = exp;
    this.exp.gmmName = 'exp multi';
    this.line = line;

    this.children.push(this.base);
    this.base.parent = this;
    this.children.push(this.exp);
    this.exp.parent = this;

    scaleFont(this.exp, this.getExponentFont(), this.getPowerScale());
  }

  private getExponentFont(): string {
    return this.exp.findAnyChildFont() || getFont();
  }

  getPaintedCharCount(): number {
    return this.base.getPaintedCharCount() + this.exp.getPaintedCharCount();
  }

  setEditable(b: boolean): void {
    this.base.setEditable(b);
    this.exp.setEditable(b);
  }

  getPowerScale(): number {
    const middleChild = this.exp.children[1] as MathObject | undefined;

    return (
      POWER_SCALE -
      (this.exp.children.length == 3 && middleChild?.t === 'fraction' ? 0.1 : 0)
    );
  }

  setFont(f: string): void {
    this.base.setFont(f);
    scaleFont(this.exp, f, this.getPowerScale());
  }

  swapString(old: string, replacer: string): void {
    this.base.swapString(old, replacer);
    this.exp.swapString(old, replacer);
  }

  hasDigitBase(): boolean {
    if (this.base.children.length > 1) return false;
    const child = this.base.children[0];

    if (!(child instanceof Text)) return false;

    return isNumeric(child.text);
  }

  setBaseAndExp(base: Multi, exp: Multi): void {
    this.children = [];
    this.children.push(base);
    this.children.push(exp);
    this.base = base;
    this.exp = exp;
    base.parent = this;
    exp.parent = this;
    scaleFont(this.exp, this.getExponentFont(), this.getPowerScale());
  }

  setLine(line: Line): void {
    this.line = line;
    this.base.setLine(line);
    this.exp.setLine(line);
  }

  toXML(): string {
    return (
      '<p><b>' + this.base.toXML() + '</b><e>' + this.exp.toXML() + '</e></p>'
    );
  }

  grabFocus(d: Direction): void {
    if (d === 'left') {
      this.exp.grabFocus(d);
    } else {
      this.base.grabFocus(d);
    }
  }

  unsureWhichToFocus(): void {
    const b = this.base.children[0] as MathObject;

    if ((b instanceof Text && b.text.length > 0) || !(b instanceof Text)) {
      this.exp.grabFocus();
    } else {
      this.base.grabFocus();
    }
  }

  buildSizeRecursive(): void {
    this.base.buildSizeRecursive();
    this.exp.buildSizeRecursive();
    this.buildSize();
  }

  buildSize(): void {
    const w = this.base.viewportW + this.exp.viewportW;
    const h = this.base.viewportH + OFFSET_RATIO * this.exp.viewportH;

    this.setAllDim(w, h);
    this.setPositionsOfChildren();
  }

  getVertToBaseline(): number {
    return this.base.getVertToBaseline() + OFFSET_RATIO * this.exp.viewportH;
  }

  setPositionsOfChildren(): void {
    this.base.viewportY = OFFSET_RATIO * this.exp.viewportH;
    this.exp.viewportX = this.base.viewportW;
  }

  childSizeChanged(): void {
    this.buildSize();
    this.getValueParent()?.childSizeChanged();
  }

  add(tile: MathObject, exp: any): void {
    if (!exp) {
      this.base.add(tile);
    } else {
      this.exp.add(tile);
    }
  }
}

export function buildPower(
  problemJS: MultiDependencies,
  line: Line,
  base?: Multi,
  exp?: Multi
): Power {
  const exponentMulti = exp || buildDefaultMulti(problemJS, line);

  // limit number of characters in a Text in an exponent
  for (const child of exponentMulti.getValueChildren()) {
    if (child instanceof Text) {
      child.setCharLimit('exponent', MAX_CHARS_IN_EXP_TEXT);
    }
  }

  return new Power(
    line,
    base || buildDefaultMulti(problemJS, line),
    exponentMulti
  );
}
